import React, { useEffect, useMemo, useState } from "react";
import {
  FiGrid,
  FiActivity,
  FiInbox,
  FiBarChart2,
  FiChevronRight,
} from "react-icons/fi";

import StatisticsCards from "./StatisticsCards";
import PagesVisitesBarChart from "./PagesVisitesBarChart";
import ClubSummaryCards from "./ClubSummaryCards";
import ClubSummaryDashboard from "./ClubSummaryDashboard";
import SaasSummaryTable from "./SaasSummaryTable";
import RequestsTable from "./RequestsTable";
import RequestsBoard from "./RequestsBoard";
import SubscriptionsCharts from "./SubscriptionsCharts";
import WebsiteSummaryTable from "./WebsiteSummaryTable";
import LightSummaryCards from "./LightSummaryCards";
import PagesVisitesMiniPanel from "./PagesVisitesLiveMonitor";
import MairieSummaryTable from "./MairieSummaryTable";
import AssoSummaryTable from "./AssoSummaryTable";

const TABS = [
  {
    key: "overview",
    label: "Vue d’ensemble",
    icon: FiGrid,
    description: "Cards, statistiques globales et graphiques principaux",
  },
  // {
  //   key: "monitoring",
  //   label: "Sites & activité",
  //   icon: FiActivity,
  //   description: "Visites, notifications et activité sur les sites gérés",
  // },
  {
    key: "requests",
    label: "Requêtes clients",
    icon: FiInbox,
    description: "Pilotage des demandes et suivi opérationnel",
  },
  {
    key: "analytics",
    label: "Analytics MonAppliClub",
    icon: FiBarChart2,
    description: "Compteur global et pages les plus visitées",
  },
];

const DashboardAdmin = () => {
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("monappliclub_dashboard_tab") || "overview";
  });

  useEffect(() => {
    localStorage.setItem("monappliclub_dashboard_tab", activeTab);
  }, [activeTab]);

  const activeTabData = useMemo(
    () => TABS.find((tab) => tab.key === activeTab) || TABS[0],
    [activeTab],
  );

  return (
    <div className="space-y-6">
      {/* HERO / HEADER */}
      <section className="rounded-[30px] border border-slate-200 bg-gradient-to-r from-[#123B57] via-[#1C567F] to-[#2EA7E0] p-6 text-white shadow-sm lg:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/90">
              Dashboard administrateur
            </div>

            <h1 className="mt-4 text-3xl font-extrabold tracking-tight lg:text-4xl">
              Tableau de bord MonAppliClub
            </h1>

            <p className="mt-3 max-w-3xl text-sm text-white/85 lg:text-base">
              Un dashboard découpé par usages pour limiter les chargements
              inutiles, améliorer la lisibilité et retrouver une expérience plus
              proche de Dougs.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <HeroMiniStat value="4" label="onglets" />
            <HeroMiniStat value="UI" label="uniformisée" />
            <HeroMiniStat value="1" label="onglet chargé" />
            <HeroMiniStat value="Dougs" label="inspiration" />
          </div>
        </div>
      </section>

      {/* TABS */}
      <section className="rounded-[26px] border border-slate-200 bg-white p-2 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`group inline-flex items-center gap-3 rounded-2xl px-4 py-3 text-left transition-all duration-200 ${
                  isActive
                    ? "bg-[#123B57] text-white shadow-[0_12px_30px_rgba(18,59,87,0.18)]"
                    : "bg-[#F8FBFD] text-slate-700 hover:bg-[#EEF7FC]"
                }`}
              >
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-xl border text-[18px] transition ${
                    isActive
                      ? "border-white/10 bg-white/10 text-white"
                      : "border-slate-200 bg-white text-slate-500"
                  }`}
                >
                  <Icon />
                </span>

                <span className="min-w-0">
                  <span className="block text-sm font-bold">{tab.label}</span>
                  <span
                    className={`block text-xs ${
                      isActive ? "text-white/80" : "text-slate-500"
                    }`}
                  >
                    {tab.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* TAB HEADER */}
      {/* <section className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
              Onglet actif
            </div>
            <h2 className="mt-1 text-2xl font-extrabold text-[#123B57]">
              {activeTabData.label}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {activeTabData.description}
            </p>
          </div>

          <div className="inline-flex items-center gap-2 rounded-full bg-[#EEF7FC] px-4 py-2 text-sm font-semibold text-[#123B57]">
            <span>Navigation</span>
            <FiChevronRight />
            <span>{activeTabData.label}</span>
          </div>
        </div>
      </section> */}

      {/* CONTENT */}
      {activeTab === "overview" && <OverviewTab />}
      {activeTab === "monitoring" && <MonitoringTab />}
      {activeTab === "requests" && <RequestsTab />}
      {activeTab === "analytics" && <AnalyticsTab />}
    </div>
  );
};

export default DashboardAdmin;

/* ---------------------------------- */
/* TABS CONTENT */
/* ---------------------------------- */

function OverviewTab() {
  return (
    <div className="space-y-6">
      <DashboardSection
        title="Indicateurs principaux"
        subtitle="Les cartes globales de ton activité commerciale et de fréquentation."
      >
        <StatisticsCards />
      </DashboardSection>

      <DashboardSection
        title="Abonnements"
        subtitle="Suivi visuel de la dynamique des abonnements."
      >
        <SubscriptionsCharts />
      </DashboardSection>
    </div>
  );
}

function MonitoringTab() {
  return (
    <div className="space-y-6">
      <DashboardSection
        title="Activité des sites MonAppliClub Standard"
        subtitle="Visites, notifications et messages reçus sur les clubs utilisant MonAppliClub Standard."
      >
        <ClubSummaryCards />
      </DashboardSection>

      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2">
        <DashboardSection
          title="MonAppliClub Light"
          subtitle="Résumé rapide des clubs utilisant la version Light."
        >
          <LightSummaryCards />
        </DashboardSection>

        <DashboardSection
          title="Synthèse SaaS"
          subtitle="Vue consolidée de l’activité de tes applications SaaS."
        >
          <SaasSummaryTable />
        </DashboardSection>
      </div>

      <DashboardSection
        title="MonAppliMairie"
        subtitle="Vue consolidée des visites et utilisateurs sur les instances mairie."
      >
        <MairieSummaryTable />
      </DashboardSection>

      <DashboardSection
        title="MonAppliAsso"
        subtitle="Vue consolidée des visites et utilisateurs sur les instances MonAppliAsso."
      >
        <AssoSummaryTable />
      </DashboardSection>

      <DashboardSection
        title="Résumé des sites"
        subtitle="Vue complémentaire par plateforme ou site géré."
      >
        <WebsiteSummaryTable />
      </DashboardSection>
    </div>
  );
}

function RequestsTab() {
  return (
    <div className="space-y-6">
      <DashboardSection
        title="Board des requêtes"
        subtitle="Visualisation rapide des demandes clients et de leur état."
      >
        <RequestsBoard />
      </DashboardSection>

      <DashboardSection
        title="Tableau détaillé"
        subtitle="Liste détaillée des requêtes pour traitement et suivi."
      >
        <RequestsTable />
      </DashboardSection>
    </div>
  );
}

function AnalyticsTab() {
  return (
    <div className="space-y-6">
      <DashboardSection
        title="Pages visitées"
        subtitle="Répartition des visites sur les différentes pages de MonAppliClub."
      >
        <PagesVisitesBarChart />
      </DashboardSection>

      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2">
        <DashboardSection
          title="Monitoring live"
          subtitle="Flux de visites récentes sur MonAppliClub."
        >
          <PagesVisitesMiniPanel pollMs={8000} initialLimit={8} />
        </DashboardSection>

        <DashboardSection
          title="Bloc complémentaire"
          subtitle="Zone libre pour ajouter ensuite un compteur global, une courbe mensuelle ou un top pages."
        >
          <AnalyticsPlaceholderCard />
        </DashboardSection>
      </div>
    </div>
  );
}

/* ---------------------------------- */
/* SHARED UI */
/* ---------------------------------- */

function DashboardSection({ title, subtitle, children }) {
  return (
    <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm lg:p-6">
      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-xl font-extrabold text-[#123B57]">{title}</h3>
          {subtitle ? (
            <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          ) : null}
        </div>
      </div>

      <div>{children}</div>
    </section>
  );
}

function HeroMiniStat({ value, label }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur">
      <div className="text-2xl font-extrabold leading-none">{value}</div>
      <div className="mt-1 text-xs font-medium uppercase tracking-[0.12em] text-white/80">
        {label}
      </div>
    </div>
  );
}

function AnalyticsPlaceholderCard() {
  return (
    <div className="flex min-h-[260px] items-center justify-center rounded-[24px] border border-dashed border-slate-300 bg-[#F8FBFD] p-8 text-center">
      <div>
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-2xl text-[#2EA7E0] shadow-sm">
          <FiBarChart2 />
        </div>
        <h4 className="mt-4 text-lg font-bold text-[#123B57]">
          Zone prête pour ton prochain bloc analytics
        </h4>
        <p className="mt-2 max-w-md text-sm text-slate-500">
          Tu pourras y ajouter ensuite un compteur total MonAppliClub, une
          courbe mensuelle, un top pages ou un comparatif période N / N-1.
        </p>
      </div>
    </div>
  );
}
